import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faBullhorn,
  faSquareCheck,
  faBookOpen,
  faHospital,
  faHandshake,
  faHouse,
} from "@fortawesome/free-solid-svg-icons";

const audienceCards = [
  {
    icon: faUsers,
    title: "Rescues",
    description:
      "Promote dogs in your care, manage adoptable dog profiles, and access tools, resources, and free advertising designed to support your rescue efforts.",
  },
  {
    icon: faBullhorn,
    title: "Networkers & Volunteers",
    description:
      "Champion urgent and at-risk dogs — share their profiles, collect foster interest, fundraise on their behalf, and tap into the same promotional tools and resources available to rescues.",
  },
];

const partnerBenefits = [
  "Create and manage dog profiles",
  "Receive foster interest forms directly for at-risk dogs",
  "Access shelter, transport, veterinary and rescue resources",
  "Connect with other Community Partners",
  "Reach more adopters with free Google Ads promotion",
];

const benefitImages = [
  "https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/benefit-1.png",
  "https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/benefit-2.png",
  "https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/benefit-3.png",
  "https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/benefit-4.png",
];

const startSteps = [
  {
    title: "Create an account",
    description:
      "Sign up to join the Community Partner network and gain access to the platform.",
  },
  {
    title: "Access partner tools & resources",
    description:
      "Explore the Community Partner dashboard, toolkit, and resources available to support your rescue efforts.",
  },
  {
    title: "Create & manage dog profiles",
    description:
      "Share adoptable dogs, receive foster inquiries, and help more people discover dogs in your care.",
  },
];

const toolkitCards = [
  {
    icon: faBookOpen,
    title: "Foster guides",
    description: "Behavioral, medical, and general foster resources.",
  },
  {
    icon: faHospital,
    title: "Rescue-Friendly Vets",
    description:
      "Find veterinary resources recommended by the rescue community.",
  },
  {
    icon: faHandshake,
    title: "Partner Directory",
    description: "Discover and connect with other Community Partners.",
  },
  {
    icon: faHouse,
    title: "Shelter Directory",
    description: "Access local shelter information and contacts.",
  },
];

const CommunityPartners: React.FC = () => {
  return (
    <section className="mb-11 font-poppins text-gray-dark">
      {/* Hero */}
      <section className="flex w-full flex-col items-center justify-center px-6 py-16 lg:px-16 lg:py-28">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-10">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-3">
              <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                For rescues, networkers &amp; shelter volunteers
              </span>
              <h1 className="text-center text-4xl font-extrabold text-black">
                Help more dogs find fosters, adopters &amp; second chances
              </h1>
            </div>
            <p className="text-center text-xl font-normal text-gray-dark">
              Join the Pup Profile Community Partner network and gain access to
              tools, resources, and a dashboard designed to help dogs get seen,
              supported, and safely placed.
            </p>
          </div>
          <Link
            to="/register"
            className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
          >
            Become a Community Partner
          </Link>
        </div>
      </section>

      {/* What are Community Partners? */}
      <section className="flex w-full flex-col items-center gap-10 px-6 py-16 lg:flex-row lg:gap-0 lg:px-16 lg:py-20">
        <div className="flex w-full flex-col gap-6 lg:flex-1 lg:px-16">
          <div className="flex flex-col gap-3">
            <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
              What are Community Partners?
            </span>
            <h2 className="text-3xl font-bold text-blue-strong">
              A network of people working together to save dogs
            </h2>
          </div>
          <p className="text-xl font-normal text-black">
            Community Partners are rescues, networkers, and shelter volunteers
            who use Pup Profile to help dogs find fosters, adopters, and rescue
            placement. Through the Community Partner Dashboard, partners can
            manage dog profiles, receive foster inquiries, access rescue
            resources, and connect with others working to save lives.
          </p>
        </div>
        <div className="w-full lg:flex-1 lg:px-16">
          <img
            src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/what-we-are.png"
            alt="A rescue dog wearing a bandana"
            className="aspect-square w-full rounded-xl object-cover"
          />
        </div>
      </section>

      {/* Who it's for */}
      <section className="flex w-full flex-col items-center gap-12 bg-blue-pale px-6 py-20 lg:px-16">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
              Who it&rsquo;s for
            </span>
            <h2 className="text-center text-3xl font-bold text-blue-strong">
              Built for rescues, networkers &amp; shelter volunteers
            </h2>
          </div>
          <p className="text-center text-base font-normal text-black">
            Whether you&rsquo;re part of a rescue, advocating for at-risk shelter
            dogs, or volunteering your time to help dogs find safe placements,
            Pup Profile provides tools, resources, and visibility to support
            your efforts.
          </p>
        </div>
        <div className="flex flex-col items-start justify-center gap-10 lg:flex-row">
          {audienceCards.map((card) => (
            <div
              key={card.title}
              className="flex max-w-[450px] flex-col items-center gap-6 p-6"
            >
              <FontAwesomeIcon icon={card.icon} className="text-[2.5rem] text-blue" />
              <div className="flex flex-col items-center gap-3">
                <h3 className="text-2xl font-semibold text-black">
                  {card.title}
                </h3>
                <p className="text-center text-base font-normal text-gray-dark">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partner benefits + toolkit */}
      <section className="flex w-full flex-col items-center gap-12 px-6 py-20 lg:px-32">
        {/* Everything you need */}
        <div className="flex w-full max-w-[1184px] flex-col items-center gap-10 lg:flex-row lg:gap-16">
          <div className="flex w-full flex-col gap-10 lg:flex-1">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                  Partner benefits
                </span>
                <h2 className="text-3xl font-bold text-blue-strong">
                  Everything you need to support more dogs
                </h2>
              </div>
              <p className="text-base font-normal text-black">
                Community Partners receive access to tools, resources, and
                support designed to help dogs get seen, connect with potential
                fosters and adopters, and move safely from shelter to home.
              </p>
            </div>
            <ul className="flex flex-col gap-4">
              {partnerBenefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faSquareCheck}
                    className="shrink-0 text-xl text-blue"
                  />
                  <span className="text-base font-normal text-black">
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid w-full grid-cols-2 gap-3 lg:flex-1">
            {benefitImages.map((src, index) => (
              <img
                key={src}
                src={src}
                alt={`Rescue dog ${index + 1}`}
                className="aspect-square w-full rounded-xl object-cover"
              />
            ))}
          </div>
        </div>

        {/* Partner toolkit */}
        <div className="flex w-full max-w-[1184px] flex-col gap-6">
          <div className="flex max-w-[700px] flex-col gap-4">
            <h3 className="text-2xl font-semibold text-gray-dark">
              Partner toolkit
            </h3>
            <p className="text-base font-normal text-black">
              Community Partners gain access to a growing library of rescue
              resources, directories, guides, and educational content designed
              to support the dogs in their care.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {toolkitCards.map((card) => (
              <div
                key={card.title}
                className="flex items-start gap-6 rounded-lg bg-blue-pale p-6"
              >
                <FontAwesomeIcon
                  icon={card.icon}
                  className="shrink-0 text-[2.5rem] text-blue"
                />
                <div className="flex flex-col gap-3">
                  <p className="text-xl font-semibold text-black">
                    {card.title}
                  </p>
                  <p className="text-base font-normal text-gray-dark">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="flex w-full flex-col items-center gap-12 px-6 py-20 lg:px-16">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
              How it works
            </span>
            <h2 className="text-center text-3xl font-bold text-blue-strong">
              Getting started is easy
            </h2>
          </div>
          <p className="text-center text-base font-normal text-black">
            Join the Pup Profile Community Partner network and start connecting
            adoptable dogs with fosters, adopters, and rescue supporters. Once
            approved, you&rsquo;ll have access to tools and resources designed to
            support your lifesaving work.
          </p>
        </div>
        <div className="flex flex-col items-start justify-center gap-10 lg:flex-row">
          {startSteps.map((step, index) => (
            <div
              key={step.title}
              className="flex max-w-[350px] flex-col items-center gap-6 p-6"
            >
              <div className="flex size-10 items-center justify-center rounded-full bg-blue text-lg font-bold text-white">
                {index + 1}
              </div>
              <div className="flex flex-col items-center gap-3">
                <h3 className="text-center text-2xl font-semibold text-black">
                  {step.title}
                </h3>
                <p className="text-center text-base font-normal text-gray-dark">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <Link
          to="/register"
          className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
        >
          Become a Community Partner
        </Link>
      </section>

      {/* Ready to get started CTA */}
      <section className="flex w-full flex-col items-stretch bg-blue-pale lg:flex-row lg:justify-between">
        <div className="flex flex-col gap-10 px-6 py-12 lg:w-[650px] lg:py-20 lg:pl-32 lg:pr-20">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                Ready to get started?
              </span>
              <h2 className="text-4xl font-extrabold text-black">
                Join a community dedicated to helping dogs
              </h2>
            </div>
            <p className="text-xl font-normal text-gray-dark">
              Whether you&rsquo;re a rescue, networker, or shelter volunteer, Pup
              Profile provides the tools and resources to help more dogs find
              safety, support, and second chances.
            </p>
          </div>
          <Link
            to="/register"
            className="flex h-12 w-fit min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
          >
            Become a Community Partner
          </Link>
        </div>
        <div className="lg:flex-1">
          <img
            src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/community-partners/read-to-start.png"
            alt="A happy rescue dog ready for a new home"
            className="h-64 w-full object-cover sm:h-96 lg:h-full"
          />
        </div>
      </section>
    </section>
  );
};

export default CommunityPartners;
